import { Span } from "@@/shared/text/span";
import { cn } from "@@/shared/utils/cn";
import { hasProp, type TranslatableType } from "@towni/common";
import type React from "react";

type Props =
    | {
          className?: string;
          /** defaults to `Listan är tom`, `List is empty` */
          text: TranslatableType;
      }
    | {
          className?: string;
          /** defaults to `Listan är tom`, `List is empty` */
          readonly children: React.ReactNode;
      };

/**
 * Renders an empty state component that displays either a text message or custom children content.
 *
 * @param props - The props for the EmptyState component.
 * @param props.text - Optional. If provided, displays this text inside the empty state.
 * @param props.className - Optional. Additional CSS classes to apply to the section.
 * @param props.children - Optional. Custom React nodes to render if `text` is not provided.
 *
 * @returns A styled section element displaying either the provided text or custom children.
 */
const EmptyState = (props: Props) => {
    if (hasProp(props, "text"))
        return (
            <section
                className={cn(
                    "border-black-300 bg-black-010 flex items-center justify-center rounded-lg border-1 border-dashed p-20 text-center text-balance",
                    props.className,
                )}>
                <Span text={props.text} className="text-neutral-copy-5" />
            </section>
        );

    return (
        <section
            className={cn(
                "border-black-300 bg-black-010 flex items-center justify-center rounded-lg border-1 border-dashed p-20",
                props.className,
            )}>
            {props.children}
        </section>
    );
};

export { EmptyState };
